import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import useBackgroundImage from '../hooks/useBackgroundImage';
import { getFullScreenBackgroundStyle } from '../utils/backgroundStyles';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const backgroundImage = useBackgroundImage();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main 
      className="min-h-screen pt-20 md:pt-24 pb-24 md:pb-32"
      style={getFullScreenBackgroundStyle(backgroundImage)}
    >
      <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center space-x-2 mb-6 text-gray-700 hover:text-gray-900 transition-colors duration-200 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-lg shadow-sm hover:shadow-md"
        >
          <FaArrowLeft className="w-4 h-4" />
          <span className="poppins font-medium">Back</span>
        </button>

        {/* Privacy Policy Content */}
        <div className="bg-white/90 backdrop-blur-md rounded-2xl shadow-2xl border border-white/30 p-6 md:p-10 lg:p-12">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 poppins mb-4">
            Privacy Policy
          </h1>
          
          <p className="text-gray-600 poppins mb-8">
            <strong>Last Updated:</strong> October 15, 2025
          </p>

          <div className="space-y-8 text-gray-700 poppins leading-relaxed">
            <p>
              Welcome to Yorokart, a digital menu and ordering platform provided by Yoro Technologies ("we", "our", or "us").
              We value your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard the information you provide when using our website or mobile application.
            </p>

            <hr className="border-gray-300" />

            {/* Section 1 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Information We Collect</h2>
              <p className="mb-3">We may collect the following types of information:</p>
              
              <h3 className="text-xl font-semibold text-gray-800 mt-4 mb-2">a. Information You Provide Directly</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Contact details:</strong> such as your name, phone number when you place an order or contact a vendor.</li>
                <li><strong>Order details:</strong> items you select, quantity, special notes, and other related data.</li>
                <li><strong>Vendor communications:</strong> when you interact with a business through WhatsApp or other channels.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-4 mb-2">b. Automatically Collected Information</h3>
              <p className="mb-2">When you visit our website or use our app, we may automatically collect:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Device information (browser type, device type, operating system)</li>
                <li>Usage data (pages viewed, time spent, actions taken)</li>
                <li>IP address and approximate location</li>
              </ul>
            </section>

            <hr className="border-gray-300" />

            {/* Section 2 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Your Information</h2>
              <p className="mb-2">We use the collected information for:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Displaying digital menus and product listings</li>
                <li>Sending your order details directly to the vendor's WhatsApp account</li>
                <li>Improving our website, user experience, and services</li>
                <li>Providing customer support and responding to inquiries</li>
                <li>Complying with legal requirements</li>
              </ul>
              <p className="mt-3 font-semibold">We do not sell, rent, or trade your personal information to any third parties.</p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 3 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. WhatsApp Integration</h2>
              <p className="mb-3">
                When you click "Place Order," Yorokart automatically opens WhatsApp to send your selected order details directly to the vendor.
              </p>
              <p className="mb-2">Please note:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Your message and phone number will be shared only with the vendor you choose to contact.</li>
                <li>Communication through WhatsApp is subject to WhatsApp's Privacy Policy and Terms of Service.</li>
                <li>Yorokart does not store or monitor any chat content exchanged via WhatsApp.</li>
              </ul>
            </section>

            <hr className="border-gray-300" />

            {/* Section 4 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Cookies and Tracking</h2>
              <p className="mb-2">We may use cookies or similar technologies to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Improve website performance</li>
                <li>Remember user preferences</li>
                <li>Analyze traffic and usage trends</li>
              </ul>
              <p className="mt-3">
                You can disable cookies in your browser settings, though some features may not function properly.
              </p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 5 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
              <p>
                We take appropriate measures to protect your information against unauthorized access, alteration, disclosure, or destruction.
                However, please note that no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 6 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Retention</h2>
              <p>
                We retain your information only for as long as necessary to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements.
              </p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 7 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Your Rights</h2>
              <p className="mb-2">Depending on your region, you may have rights to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Access or correct your information</li>
                <li>Request deletion of your data</li>
                <li>Withdraw consent for certain uses</li>
              </ul>
              <p className="mt-3">
                You can make such requests by contacting us at <a href="mailto:info@yorotechnologies.com" className="text-blue-600 hover:text-blue-800 underline">info@yorotechnologies.com</a>.
              </p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 8 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Third-Party Links</h2>
              <p>
                Our platform may contain links to third-party websites (e.g., vendors' social media or websites).
                We are not responsible for their privacy practices. Please review their policies before sharing any information.
              </p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 9 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Children's Privacy</h2>
              <p>
                Yorokart is not intended for individuals under 13 years of age. We do not knowingly collect data from children.
              </p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 10 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Updates to This Policy</h2>
              <p>
                We may update this Privacy Policy periodically. Any changes will be posted on this page with a revised "Last Updated" date.
              </p>
            </section>

            <hr className="border-gray-300" />

            {/* Section 11 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact Us</h2>
              <p className="mb-3">If you have any questions or concerns about this Privacy Policy, please contact us:</p>
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <p className="font-semibold text-gray-900 text-lg mb-3">Yoro Technologies</p>
                <p className="mb-2"><strong>Email:</strong> <a href="mailto:info@yorotechnologies.com" className="text-blue-600 hover:text-blue-800 underline">info@yorotechnologies.com</a></p>
                <p className="mb-2"><strong>Website:</strong> <a href="https://www.yorotechnologies.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">www.yorotechnologies.com</a></p>
                <p><strong>Location:</strong> Calicut, Kerala, India</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </main>
  );
};

export default PrivacyPolicy;

